import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import {
    getSingleNonErrorOutputRoleName,
    readMaterializedBooleanDecision,
} from '@toolproof-core/lib/_pure/macro-step-decisions';
import {
    getAuthoringSteps,
} from '@toolproof-core/lib/_pure/mutable-workflow-overlay';
import type {
    MutableBranchStepKey,
    MutableLoopStepKey,
    MutableMacroStepSlot,
    MutableTickTracePoint,
    MutableToolStep,
    MutableToolStepKey,
    MutableWorkflow,
    MutableWorkflowState,
    MutableWorkflowStateEntryByRoleName,
    MutableWorkflowTrace,
} from '@toolproof-core/lib/_contract/workflow-overlay';
import type { ToolMap } from '@toolproof-core/lib/_pure/macro-step-decisions';

import {
    deriveRootLoopStepKeyByCloneLoopStepKey,
    mergeWorkflowStateDelta,
    normalizeLoopOwnerStepKey,
    toBranchCaseAddressKey,
} from '@/workflow-runner/_pure/model/traceHelpers';
import type {
    TraceSnapshot,
    TraceSnapshotAuthoredBranchStepControl,
    TraceSnapshotEdgeEventKind,
} from '@/workflow-runner/_contract/traceSnapshot';

type BaseTraceSnapshot = {
    selectedTraceHandle: string;
    mutableWorkflowTrace: MutableWorkflowTrace;
    tickTracePoints: MutableTickTracePoint[];
    timeline: {
        preToolPreludeTickCount: number;
        toolExecutionTickIndexes: number[];
    };
    initialWorkflow: {
        mutableWorkflow: MutableWorkflow | null;
        workflowState: MutableWorkflowState;
    };
    macroStepControl: {
        branch: {
            authored: TraceSnapshotAuthoredBranchStepControl;
        };
    };
};

function deriveOrderedToolStepsFromMutableWorkflow(mutableWorkflow: MutableWorkflow | null): MutableToolStep[] {
    const authoringSteps = mutableWorkflow
        ? getAuthoringSteps(mutableWorkflow)
        : [];

    const toolSteps: MutableToolStep[] = [];
    const seenToolStepKeys = new Set<MutableToolStep['stepKey']>();
    const pushToolStep = (toolStep: MutableToolStep | undefined) => {
        if (!toolStep) return;
        if (seenToolStepKeys.has(toolStep.stepKey)) return;
        toolSteps.push(toolStep);
        seenToolStepKeys.add(toolStep.stepKey);
    };

    for (const step of authoringSteps) {
        if (step.stepKind === CONSTANTS.Enums.StepKind.tool) {
            pushToolStep(step);
            continue;
        }

        if (step.stepKind === CONSTANTS.Enums.StepKind.branch) {
            for (const branchCase of step.cases) {
                if (!branchCase) continue;
                pushToolStep(branchCase.when as MutableToolStep);
                pushToolStep(branchCase.what as MutableToolStep);
            }
            continue;
        }

        // Loop cases are intentionally not flattened from nested structure.
        // Loop tool steps become visible only after loop_continuation inserts them
        // at the top level, preventing duplicate nested + top-level traversal.
    }

    return toolSteps;
}

function deriveLoopToolStepsFromReplayTicks(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): MutableToolStep[] {
    const { tickTracePoints, appliedTickCount } = args;
    const loopToolSteps: MutableToolStep[] = [];
    const seenToolStepKeys = new Set<MutableToolStep['stepKey']>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;

        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.loop_continuation) continue;
        const stepsMutation = tracePoint.delta?.stepsMutation;
        if (!stepsMutation) continue;

        for (const insertedStep of stepsMutation.insertedSteps) {
            if (insertedStep.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;

            const typedToolStep = insertedStep as MutableToolStep;
            if (seenToolStepKeys.has(typedToolStep.stepKey)) continue;

            loopToolSteps.push(typedToolStep);
            seenToolStepKeys.add(typedToolStep.stepKey);
        }
    }

    return loopToolSteps;
}

function deriveLoopToolStepSlotSetsFromReplayTicks(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): {
    loopWhatToolStepKeys: Set<MutableToolStepKey>;
    loopWhenToolStepKeys: Set<MutableToolStepKey>;
} {
    const { tickTracePoints, appliedTickCount } = args;
    const loopWhatToolStepKeys = new Set<MutableToolStepKey>();
    const loopWhenToolStepKeys = new Set<MutableToolStepKey>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.loop_continuation) continue;

        const stepsMutation = tracePoint.delta?.stepsMutation;
        if (!stepsMutation) continue;

        const insertedToolSteps = stepsMutation.insertedSteps.filter(
            (insertedStep): insertedStep is MutableToolStep =>
                insertedStep.stepKind === CONSTANTS.Enums.StepKind.tool
        );

        const insertedWhatToolStep = insertedToolSteps[0];
        const insertedWhenToolStep = insertedToolSteps[1];

        if (insertedWhatToolStep) {
            loopWhatToolStepKeys.add(insertedWhatToolStep.stepKey as MutableToolStepKey);
        }

        if (insertedWhenToolStep) {
            loopWhenToolStepKeys.add(insertedWhenToolStep.stepKey as MutableToolStepKey);
        }
    }

    return {
        loopWhatToolStepKeys,
        loopWhenToolStepKeys,
    };
}

function deriveBranchToolStepControlFromReplayTicks(args: {
    initialMutableWorkflow: MutableWorkflow | null;
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): {
    branchToolStepKeyByAddress: Map<string, MutableToolStep['stepKey']>;
    branchCaseIndexByToolStepKey: Map<MutableToolStep['stepKey'], number>;
    branchOwnerByToolStepKey: Map<MutableToolStep['stepKey'], MutableBranchStepKey>;
    branchWhatToolStepKeys: Set<MutableToolStepKey>;
    branchWhenToolStepKeys: Set<MutableToolStepKey>;
} {
    const { initialMutableWorkflow } = args;
    const branchToolStepKeyByAddress = new Map<string, MutableToolStep['stepKey']>();
    const branchCaseIndexByToolStepKey = new Map<MutableToolStep['stepKey'], number>();
    const branchOwnerByToolStepKey = new Map<MutableToolStep['stepKey'], MutableBranchStepKey>();
    const branchWhatToolStepKeys = new Set<MutableToolStepKey>();
    const branchWhenToolStepKeys = new Set<MutableToolStepKey>();

    if (initialMutableWorkflow) {
        for (const step of getAuthoringSteps(initialMutableWorkflow)) { // ATTENTION: getAuthoringSteps is meant for builder mode
            if (step.stepKind !== CONSTANTS.Enums.StepKind.branch) continue;

            const ownerBranchStepKey = step.stepKey as MutableBranchStepKey;
            if (!ownerBranchStepKey.startsWith('BRANCH_STEP-')) continue;

            for (let ownerCaseIndex = 0; ownerCaseIndex < step.cases.length; ownerCaseIndex += 1) {
                const branchCase = step.cases[ownerCaseIndex];
                if (!branchCase) continue;

                const whenStepKey = branchCase.when.stepKey as MutableToolStepKey;
                branchToolStepKeyByAddress.set(
                    toBranchCaseAddressKey({
                        ownerBranchStepKey,
                        ownerCaseIndex,
                        slotInOwner: 'cases/when',
                    }),
                    whenStepKey
                );
                branchCaseIndexByToolStepKey.set(whenStepKey, ownerCaseIndex);
                branchOwnerByToolStepKey.set(whenStepKey, ownerBranchStepKey);
                branchWhenToolStepKeys.add(whenStepKey);

                const whatStepKey = branchCase.what.stepKey as MutableToolStepKey;
                branchToolStepKeyByAddress.set(
                    toBranchCaseAddressKey({
                        ownerBranchStepKey,
                        ownerCaseIndex,
                        slotInOwner: 'cases/what',
                    }),
                    whatStepKey
                );
                branchCaseIndexByToolStepKey.set(whatStepKey, ownerCaseIndex);
                branchOwnerByToolStepKey.set(whatStepKey, ownerBranchStepKey);
                branchWhatToolStepKeys.add(whatStepKey);
            }
        }
    }

    // ATTENTION: can probably be removed
    /* const nextCaseIndexByOwner = new Map<MutableBranchStepKey, number>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.branch_continuation) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.branch) continue;

        const ownerBranchStepKey = tracePoint.stepKey as MutableBranchStepKey;
        if (!ownerBranchStepKey.startsWith('BRANCH_STEP-')) continue;

        const insertedToolSteps = (tracePoint.delta?.stepsMutation?.insertedSteps ?? []).filter(
            (insertedStep): insertedStep is MutableToolStep =>
                insertedStep.stepKind === CONSTANTS.Enums.StepKind.tool
        );
        const insertedWhenToolStep = insertedToolSteps[0];
        const insertedWhatToolStep = insertedToolSteps[1];
        if (!insertedWhenToolStep && !insertedWhatToolStep) continue;

        const explicitOwnerCaseIndex = tracePoint.macroStepContext.ownerCaseIndex;
        const derivedOwnerCaseIndex = nextCaseIndexByOwner.get(ownerBranchStepKey) ?? 0;
        const ownerCaseIndex = typeof explicitOwnerCaseIndex === 'number'
            ? explicitOwnerCaseIndex
            : derivedOwnerCaseIndex;

        if (insertedWhenToolStep) {
            const whenStepKey = insertedWhenToolStep.stepKey as MutableToolStepKey;
            branchToolStepKeyByAddress.set(
                toBranchCaseAddressKey({
                    ownerBranchStepKey,
                    ownerCaseIndex,
                    slotInOwner: 'cases/when',
                }),
                whenStepKey
            );
            branchCaseIndexByToolStepKey.set(whenStepKey, ownerCaseIndex);
            branchOwnerByToolStepKey.set(whenStepKey, ownerBranchStepKey);
            branchWhenToolStepKeys.add(whenStepKey);
        }

        if (insertedWhatToolStep) {
            const whatStepKey = insertedWhatToolStep.stepKey as MutableToolStepKey;
            branchToolStepKeyByAddress.set(
                toBranchCaseAddressKey({
                    ownerBranchStepKey,
                    ownerCaseIndex,
                    slotInOwner: 'cases/what',
                }),
                whatStepKey
            );
            branchCaseIndexByToolStepKey.set(whatStepKey, ownerCaseIndex);
            branchOwnerByToolStepKey.set(whatStepKey, ownerBranchStepKey);
            branchWhatToolStepKeys.add(whatStepKey);
        }

        const nextCaseIndex = Math.max(derivedOwnerCaseIndex, ownerCaseIndex) + 1;
        nextCaseIndexByOwner.set(ownerBranchStepKey, nextCaseIndex);
    } */

    return {
        branchToolStepKeyByAddress,
        branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey,
        branchWhatToolStepKeys,
        branchWhenToolStepKeys,
    };
}

function applyRollingLoopRebindingToWorkflowState(args: {
    workflowState: MutableWorkflowState;
    initialMutableWorkflow: MutableWorkflow | null;
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): MutableWorkflowState {
    const { workflowState, initialMutableWorkflow, tickTracePoints, appliedTickCount } = args;
    if (!initialMutableWorkflow) return workflowState;
    const rootLoopStepKeyByCloneLoopStepKey = deriveRootLoopStepKeyByCloneLoopStepKey({
        tickTracePoints,
        appliedTickCount,
    });
    const latestWhatToolStepKeyByRoot = new Map<MutableLoopStepKey, MutableToolStepKey>();
    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) continue;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.tool_execution) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;
        if (tracePoint.macroStepContext.slotInOwner !== 'case/what') continue;

        const rootLoopStepKey = normalizeLoopOwnerStepKey({
            ownerStepKey: tracePoint.macroStepContext.ownerMacroStepKey,
            rootLoopStepKeyByCloneLoopStepKey,
        });
        if (rootLoopStepKey) {
            latestWhatToolStepKeyByRoot.set(rootLoopStepKey, tracePoint.stepKey as MutableToolStepKey);
        }
    }

    let nextWorkflowState = workflowState;
    for (const threadSteps of initialMutableWorkflow.stepsByThreadIndex) {
        for (const [loopIndex, loopStep] of threadSteps.entries()) {
            if (loopStep.stepKind !== 'while' && loopStep.stepKind !== 'for') continue;
            const latestWhatToolStepKey = latestWhatToolStepKeyByRoot.get(loopStep.stepKey);
            if (!latestWhatToolStepKey) continue;

            // Eligibility comes from authored downstream bindings, never historical iteration inputs.
            for (const downstreamStep of threadSteps.slice(loopIndex + 1)) {
                const toolSteps = downstreamStep.stepKind === 'tool'
                    ? [downstreamStep]
                    : downstreamStep.stepKind === 'branch'
                        ? downstreamStep.cases.flatMap(({ when, what }) => [when, what])
                        : [downstreamStep.case.when, downstreamStep.case.what];
                for (const toolStep of toolSteps) {
                    for (const roleName of toolStep.roleBindingSpec?.inputBindings ?? []) {
                        const initialEntry = initialMutableWorkflow.workflowState[toolStep.stepKey]?.[roleName];
                        if (initialEntry?.workflowStateEntryKind !== 'internalInputPotential') continue;
                        if (initialEntry.toolStepRoleAddress.stepKey !== loopStep.case.what.stepKey) continue;
                        if (!(loopStep.case.what.roleBindingSpec?.outputBindings ?? []).includes(
                            initialEntry.toolStepRoleAddress.roleName
                        )) continue;

                        const entry = nextWorkflowState[toolStep.stepKey]?.[roleName];
                        if (entry?.workflowStateEntryKind !== 'internalInputPotential') continue;
                        if (entry.toolStepRoleAddress.stepKey === latestWhatToolStepKey) continue;
                        nextWorkflowState = {
                            ...nextWorkflowState,
                            [toolStep.stepKey]: {
                                ...nextWorkflowState[toolStep.stepKey],
                                [roleName]: {
                                    ...entry,
                                    toolStepRoleAddress: {
                                        ...entry.toolStepRoleAddress,
                                        stepKey: latestWhatToolStepKey,
                                    },
                                },
                            },
                        };
                    }
                }
            }
        }
    }

    return nextWorkflowState;
}

function remapWorkflowStateDeltaToBranchKeys(args: {
    workflowStateDelta?: Partial<MutableWorkflowState>;
    runtimeToBranchToolStepKey: ReadonlyMap<MutableToolStepKey, MutableToolStep['stepKey']>;
}): Partial<MutableWorkflowState> | undefined {
    const { workflowStateDelta, runtimeToBranchToolStepKey } = args;
    if (!workflowStateDelta) return undefined;

    const remapped: Partial<MutableWorkflowState> = {};

    for (const [runtimeToolStepKey, workflowStateEntryByRoleName] of Object.entries(workflowStateDelta)) {
        if (!workflowStateEntryByRoleName) continue;

        const branchToolStepKey = (
            runtimeToBranchToolStepKey.get(runtimeToolStepKey as MutableToolStepKey)
            ?? runtimeToolStepKey
        ) as MutableToolStepKey;

        const nextRoleEntries: MutableWorkflowStateEntryByRoleName = {};
        for (const [roleName, workflowStateEntry] of Object.entries(workflowStateEntryByRoleName)) {
            if (!workflowStateEntry) continue;

            if (workflowStateEntry.workflowStateEntryKind === 'internalInputPotential') {
                const sourceStepKey = workflowStateEntry.toolStepRoleAddress.stepKey as MutableToolStepKey;
                const remappedSourceStepKey = runtimeToBranchToolStepKey.get(sourceStepKey) ?? sourceStepKey;
                nextRoleEntries[roleName as keyof MutableWorkflowStateEntryByRoleName] = {
                    ...workflowStateEntry,
                    toolStepRoleAddress: {
                        ...workflowStateEntry.toolStepRoleAddress,
                        stepKey: remappedSourceStepKey,
                    },
                };
                continue;
            }

            nextRoleEntries[roleName as keyof MutableWorkflowStateEntryByRoleName] = workflowStateEntry;
        }

        remapped[branchToolStepKey] = {
            ...(remapped[branchToolStepKey] ?? {}),
            ...nextRoleEntries,
        };
    }

    return remapped;
}

function deriveInitialWorkflowStateFromMutableTrace(mutableWorkflowTrace: MutableWorkflowTrace): MutableWorkflowState {
    const workflowStartTracePoint = mutableWorkflowTrace.tracePoints.find(
        (tracePoint) => tracePoint.tracePointKind === 'workflow_start'
    );

    const workflowState: MutableWorkflowState =
        workflowStartTracePoint
            && 'workflow' in workflowStartTracePoint
            && workflowStartTracePoint.workflow
            && typeof workflowStartTracePoint.workflow === 'object'
            && 'workflowState' in workflowStartTracePoint.workflow
            ? ((workflowStartTracePoint.workflow.workflowState as MutableWorkflowState) ?? {})
            : {};

    return workflowState;
}

function clampReplayPositionIndex(replayPositionIndex: number | undefined, replayPositionCount: number): number {
    if (replayPositionIndex === undefined) return replayPositionCount;
    const normalized = Math.trunc(replayPositionIndex);
    if (!Number.isFinite(normalized)) return replayPositionCount;
    return Math.min(Math.max(normalized, 0), replayPositionCount);
}

function computePreToolPreludeTickCount(args: {
    tickTracePoints: MutableTickTracePoint[];
}): number {
    const { tickTracePoints } = args;

    for (let index = 0; index < tickTracePoints.length; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;

        if (
            tracePoint.tickKind === CONSTANTS.Enums.TickKind.tool_execution
            && tracePoint.stepKind === CONSTANTS.Enums.StepKind.tool
        ) {
            return index;
        }
    }

    return tickTracePoints.length;
}

function toAppliedTickCountForReplayPosition(args: {
    tickTracePoints: MutableTickTracePoint[];
    preToolPreludeTickCount: number;
    toolExecutionTickIndexes: number[];
    appliedReplayPositionIndex: number;
}): number {
    const {
        tickTracePoints,
        preToolPreludeTickCount,
        toolExecutionTickIndexes,
        appliedReplayPositionIndex,
    } = args;

    if (appliedReplayPositionIndex <= 0) return preToolPreludeTickCount;

    const toolPositionCount = toolExecutionTickIndexes.length;
    if (appliedReplayPositionIndex >= toolPositionCount) return tickTracePoints.length;

    const nextToolTickIndex = toolExecutionTickIndexes[appliedReplayPositionIndex];
    return nextToolTickIndex ?? tickTracePoints.length;
}

export function buildRuntimeToBranchToolStepKey(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
    branchToolStepKeyByAddress: ReadonlyMap<string, MutableToolStep['stepKey']>;
}): Map<MutableToolStepKey, MutableToolStep['stepKey']> {
    const { tickTracePoints, appliedTickCount, branchToolStepKeyByAddress } = args;
    const runtimeToBranch = new Map<MutableToolStepKey, MutableToolStep['stepKey']>();
    const continuationByBranchKey = new Map<string, { ownerBranchStepKey: MutableBranchStepKey; ownerCaseIndex: number }>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind === CONSTANTS.Enums.TickKind.branch_continuation
            && tracePoint.stepKind === CONSTANTS.Enums.StepKind.branch) {
            const continuation = continuationByBranchKey.get(tracePoint.stepKey) ?? {
                ownerBranchStepKey: tracePoint.stepKey as MutableBranchStepKey,
                ownerCaseIndex: 0,
            };
            const insertedSteps = tracePoint.delta?.stepsMutation?.insertedSteps ?? [];
            const members = insertedSteps.filter((step): step is MutableToolStep => step.stepKind === 'tool');
            // Continuation branches contain newly keyed copies of the remaining authored cases.
            // Map both relocated members even when a skipped WHAT never executes.
            const slots: readonly Extract<MutableMacroStepSlot, 'cases/when' | 'cases/what'>[] = ['cases/when', 'cases/what'];
            for (const [memberIndex, slotInOwner] of slots.entries()) {
                const member = members[memberIndex];
                const authoredKey = branchToolStepKeyByAddress.get(toBranchCaseAddressKey({
                    ...continuation,
                    slotInOwner,
                }));
                if (member && authoredKey) runtimeToBranch.set(member.stepKey, authoredKey);
            }
            const nextBranch = insertedSteps.find((step) => step.stepKind === 'branch');
            if (nextBranch) continuationByBranchKey.set(nextBranch.stepKey, {
                ownerBranchStepKey: continuation.ownerBranchStepKey,
                ownerCaseIndex: continuation.ownerCaseIndex + 1,
            });
        }
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.tool_execution) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;

        const ownerMacroStepKey = tracePoint.macroStepContext.ownerMacroStepKey;
        const ownerCaseIndex = tracePoint.macroStepContext.ownerCaseIndex;
        const slotInOwner = tracePoint.macroStepContext.slotInOwner;

        if (typeof ownerMacroStepKey !== 'string' || !ownerMacroStepKey.startsWith('BRANCH_STEP-')) continue;
        if (typeof ownerCaseIndex !== 'number') continue;
        if (slotInOwner !== 'cases/when' && slotInOwner !== 'cases/what') continue;

        const branchToolStepKey = branchToolStepKeyByAddress.get(
            toBranchCaseAddressKey({
                ownerBranchStepKey: ownerMacroStepKey as MutableBranchStepKey,
                ownerCaseIndex,
                slotInOwner: slotInOwner as Extract<MutableMacroStepSlot, 'cases/when' | 'cases/what'>,
            })
        );
        if (!branchToolStepKey) continue;

        runtimeToBranch.set(
            tracePoint.stepKey as MutableToolStepKey,
            branchToolStepKey
        );
    }

    return runtimeToBranch;
}

function foldWorkflowStateToReplayPosition(args: {
    initialWorkflowState: MutableWorkflowState;
    initialMutableWorkflow: MutableWorkflow | null;
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
    runtimeToBranchToolStepKey: ReadonlyMap<MutableToolStepKey, MutableToolStep['stepKey']>;
}): MutableWorkflowState {
    const {
        initialWorkflowState,
        initialMutableWorkflow,
        tickTracePoints,
        appliedTickCount,
        runtimeToBranchToolStepKey,
    } = args;
    let workflowState: MutableWorkflowState = { ...initialWorkflowState };

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;

        workflowState = mergeWorkflowStateDelta({
            workflowState,
            workflowStateDelta: remapWorkflowStateDeltaToBranchKeys({
                workflowStateDelta: tracePoint.delta?.workflowStateDelta as Partial<MutableWorkflowState> | undefined,
                runtimeToBranchToolStepKey,
            }),
        });
    }

    workflowState = applyRollingLoopRebindingToWorkflowState({
        workflowState,
        initialMutableWorkflow,
        tickTracePoints,
        appliedTickCount,
    });

    return workflowState;
}

function applyStepsMutationToMutableWorkflow(args: {
    mutableWorkflow: MutableWorkflow;
    threadIndex: number;
    insertAt: number;
    insertedSteps: MutableWorkflow['stepsByThreadIndex'][number];
}): MutableWorkflow {
    const { mutableWorkflow, threadIndex, insertAt, insertedSteps } = args;

    const nextStepsByThreadIndex = [...mutableWorkflow.stepsByThreadIndex];
    const threadSteps = nextStepsByThreadIndex[threadIndex] ?? [];
    const safeInsertAt = Math.max(0, Math.min(insertAt, threadSteps.length));

    nextStepsByThreadIndex[threadIndex] = [
        ...threadSteps.slice(0, safeInsertAt),
        ...insertedSteps,
        ...threadSteps.slice(safeInsertAt),
    ];

    return {
        ...mutableWorkflow,
        stepsByThreadIndex: nextStepsByThreadIndex,
    };
}

function foldOrderedToolStepsToReplayPosition(args: {
    initialMutableWorkflow: MutableWorkflow | null;
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
}): MutableToolStep[] {
    const { initialMutableWorkflow, tickTracePoints, appliedTickCount } = args;
    if (!initialMutableWorkflow) return [];

    let mutableWorkflow = initialMutableWorkflow;

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;

        const stepsMutation = tracePoint.delta?.stepsMutation;
        if (!stepsMutation) continue;
        if (
            tracePoint.stepKind === CONSTANTS.Enums.StepKind.branch
            && tracePoint.tickKind === CONSTANTS.Enums.TickKind.branch_continuation
        ) {
            continue;
        }

        mutableWorkflow = applyStepsMutationToMutableWorkflow({
            mutableWorkflow,
            threadIndex: tracePoint.threadIndex,
            insertAt: stepsMutation.insertAt,
            insertedSteps: stepsMutation.insertedSteps,
        });
    }

    const rawOrderedToolSteps = deriveOrderedToolStepsFromMutableWorkflow(mutableWorkflow);
    const loopToolSteps = deriveLoopToolStepsFromReplayTicks({
        tickTracePoints,
        appliedTickCount,
    });
    if (loopToolSteps.length === 0) return rawOrderedToolSteps;

    const loopToolStepKeySet = new Set(loopToolSteps.map((toolStep) => toolStep.stepKey));
    const nonLoopToolSteps = rawOrderedToolSteps.filter((toolStep) => !loopToolStepKeySet.has(toolStep.stepKey));

    return [
        ...loopToolSteps,
        ...nonLoopToolSteps,
    ];
}

function toToolStepByKey(toolSteps: MutableToolStep[]): Map<MutableToolStep['stepKey'], MutableToolStep> {
    const toolStepByKey = new Map<MutableToolStep['stepKey'], MutableToolStep>();
    for (const toolStep of toolSteps) {
        if (toolStepByKey.has(toolStep.stepKey)) continue;
        toolStepByKey.set(toolStep.stepKey, toolStep);
    }
    return toolStepByKey;
}

function readWhenToolStepDecision(args: {
    runtimeWhenToolStepKey: MutableToolStepKey;
    runtimeToBranchToolStepKey: ReadonlyMap<MutableToolStepKey, MutableToolStep['stepKey']>;
    toolStepByKey: ReadonlyMap<MutableToolStep['stepKey'], MutableToolStep>;
    workflowState: MutableWorkflowState;
    toolMap: ToolMap;
}): { whenToolStepKey: MutableToolStep['stepKey']; decision: boolean } | undefined {
    const {
        runtimeWhenToolStepKey,
        runtimeToBranchToolStepKey,
        toolStepByKey,
        workflowState,
        toolMap,
    } = args;

    const whenToolStepKey = runtimeToBranchToolStepKey.get(runtimeWhenToolStepKey) ?? runtimeWhenToolStepKey;
    const whenToolStep = toolStepByKey.get(whenToolStepKey);
    if (!whenToolStep) return undefined;

    return {
        whenToolStepKey,
        decision: readMaterializedBooleanDecision(workflowState, whenToolStep, toolMap),
    };
}

function deriveWinningBranchCaseIndexByOwner(args: {
    tickTracePoints: MutableTickTracePoint[];
    appliedTickCount: number;
    workflowState: MutableWorkflowState;
    toolStepByKey: ReadonlyMap<MutableToolStep['stepKey'], MutableToolStep>;
    toolMap: ToolMap;
    runtimeToBranchToolStepKey: ReadonlyMap<MutableToolStepKey, MutableToolStep['stepKey']>;
    branchCaseIndexByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], number>;
    branchOwnerByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], MutableBranchStepKey>;
}): Map<MutableBranchStepKey, number> {
    const {
        tickTracePoints,
        appliedTickCount,
        workflowState,
        toolStepByKey,
        toolMap,
        runtimeToBranchToolStepKey,
        branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey,
    } = args;

    const winningCaseIndexByOwner = new Map<MutableBranchStepKey, number>();

    for (let index = 0; index < appliedTickCount; index += 1) {
        const tracePoint = tickTracePoints[index];
        if (!tracePoint) break;
        if (tracePoint.tickKind !== CONSTANTS.Enums.TickKind.tool_execution) continue;
        if (tracePoint.stepKind !== CONSTANTS.Enums.StepKind.tool) continue;
        if (tracePoint.macroStepContext.slotInOwner !== 'cases/when') continue;

        const resolved = readWhenToolStepDecision({
            runtimeWhenToolStepKey: tracePoint.stepKey as MutableToolStepKey,
            runtimeToBranchToolStepKey,
            toolStepByKey,
            workflowState,
            toolMap,
        });
        if (!resolved || !resolved.decision) continue;

        const ownerBranchStepKey = branchOwnerByToolStepKey.get(resolved.whenToolStepKey);
        const winningCaseIndex = branchCaseIndexByToolStepKey.get(resolved.whenToolStepKey);
        if (!ownerBranchStepKey || winningCaseIndex === undefined) continue;

        const existingWinningCaseIndex = winningCaseIndexByOwner.get(ownerBranchStepKey);
        // Math.max collapses repeated runtime occurrences of one authored branch; see nesting TODO.
        winningCaseIndexByOwner.set(
            ownerBranchStepKey,
            existingWinningCaseIndex === undefined
                ? winningCaseIndex
                : Math.max(existingWinningCaseIndex, winningCaseIndex)
        );
    }

    return winningCaseIndexByOwner;
}

// Mirrors NodeBranchExitRebind: downstream inputs bound to the default case's WHAT output are
// repointed to the winning case's WHAT output. Synthesized at the WHEN position so the whole
// branch event animates on one edge, ahead of the engine's own rebind tick.
function applyBranchExitRebindToWorkflowState(args: {
    workflowState: MutableWorkflowState;
    orderedToolSteps: MutableToolStep[];
    winningCaseIndexByOwner: ReadonlyMap<MutableBranchStepKey, number>;
    toolStepByKey: ReadonlyMap<MutableToolStep['stepKey'], MutableToolStep>;
    branchToolStepKeyByAddress: ReadonlyMap<string, MutableToolStep['stepKey']>;
    branchCaseIndexByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], number>;
    branchOwnerByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], MutableBranchStepKey>;
}): MutableWorkflowState {
    const {
        workflowState,
        orderedToolSteps,
        winningCaseIndexByOwner,
        toolStepByKey,
        branchToolStepKeyByAddress,
        branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey,
    } = args;

    if (winningCaseIndexByOwner.size === 0) return workflowState;

    const defaultCaseIndexByOwner = new Map<MutableBranchStepKey, number>();
    for (const [toolStepKey, ownerBranchStepKey] of branchOwnerByToolStepKey) {
        const caseIndex = branchCaseIndexByToolStepKey.get(toolStepKey);
        if (caseIndex === undefined) continue;
        const existing = defaultCaseIndexByOwner.get(ownerBranchStepKey);
        defaultCaseIndexByOwner.set(
            ownerBranchStepKey,
            existing === undefined ? caseIndex : Math.max(existing, caseIndex)
        );
    }

    const rebindTargetBySourceKey = new Map<string, { stepKey: MutableToolStep['stepKey']; roleName: string }>();
    for (const [ownerBranchStepKey, winningCaseIndex] of winningCaseIndexByOwner) {
        const defaultCaseIndex = defaultCaseIndexByOwner.get(ownerBranchStepKey);
        if (defaultCaseIndex === undefined || defaultCaseIndex === winningCaseIndex) continue;

        const toWhatToolStep = (ownerCaseIndex: number): MutableToolStep | undefined => {
            const whatToolStepKey = branchToolStepKeyByAddress.get(
                toBranchCaseAddressKey({ ownerBranchStepKey, ownerCaseIndex, slotInOwner: 'cases/what' })
            );
            return whatToolStepKey === undefined ? undefined : toolStepByKey.get(whatToolStepKey);
        };

        const defaultWhatToolStep = toWhatToolStep(defaultCaseIndex);
        const winningWhatToolStep = toWhatToolStep(winningCaseIndex);
        if (!defaultWhatToolStep || !winningWhatToolStep) continue;

        rebindTargetBySourceKey.set(
            `${defaultWhatToolStep.stepKey}|${getSingleNonErrorOutputRoleName(defaultWhatToolStep)}`,
            {
                stepKey: winningWhatToolStep.stepKey,
                roleName: getSingleNonErrorOutputRoleName(winningWhatToolStep),
            }
        );
    }

    if (rebindTargetBySourceKey.size === 0) return workflowState;

    let nextWorkflowState = workflowState;
    for (const toolStep of orderedToolSteps) {
        const inputRoleNames = toolStep.roleBindingSpec?.inputBindings ?? [];
        for (const inputRoleName of inputRoleNames) {
            const entry = nextWorkflowState[toolStep.stepKey]?.[inputRoleName];
            if (!entry) continue;
            if (entry.workflowStateEntryKind !== CONSTANTS.Enums.WorkflowStateEntryKind.internalInputPotential) continue;

            const sourceAddress = entry.toolStepRoleAddress;
            const rebindTarget = rebindTargetBySourceKey.get(`${sourceAddress.stepKey}|${sourceAddress.roleName}`);
            if (!rebindTarget) continue;

            nextWorkflowState = {
                ...nextWorkflowState,
                [toolStep.stepKey]: {
                    ...nextWorkflowState[toolStep.stepKey],
                    [inputRoleName]: {
                        workflowStateEntryKind: CONSTANTS.Enums.WorkflowStateEntryKind.internalInputPotential,
                        toolStepRoleAddress: {
                            stepKey: rebindTarget.stepKey,
                            roleName: rebindTarget.roleName,
                        },
                    },
                } as MutableWorkflowStateEntryByRoleName,
            };
        }
    }

    return nextWorkflowState;
}

function deriveEdgeEventKindForReplayPosition(args: {    tickTracePoints: MutableTickTracePoint[];
    toolExecutionTickIndexes: number[];
    appliedReplayPositionIndex: number;
    workflowState: MutableWorkflowState;
    toolStepByKey: ReadonlyMap<MutableToolStep['stepKey'], MutableToolStep>;
    toolMap: ToolMap;
    runtimeToBranchToolStepKey: ReadonlyMap<MutableToolStepKey, MutableToolStep['stepKey']>;
}): TraceSnapshotEdgeEventKind {
    const {
        tickTracePoints,
        toolExecutionTickIndexes,
        appliedReplayPositionIndex,
        workflowState,
        toolStepByKey,
        toolMap,
        runtimeToBranchToolStepKey,
    } = args;

    if (appliedReplayPositionIndex <= 0) return 'none';

    const causingToolTickIndex = toolExecutionTickIndexes[appliedReplayPositionIndex - 1];
    if (causingToolTickIndex === undefined) return 'none';

    const tracePoint = tickTracePoints[causingToolTickIndex];
    if (!tracePoint) return 'none';

    const slotInOwner = tracePoint.macroStepContext.slotInOwner;
    if (slotInOwner !== 'cases/when' && slotInOwner !== 'case/when') return 'none';

    const resolved = readWhenToolStepDecision({
        runtimeWhenToolStepKey: tracePoint.stepKey as MutableToolStepKey,
        runtimeToBranchToolStepKey,
        toolStepByKey,
        workflowState,
        toolMap,
    });
    if (!resolved || !resolved.decision) return 'none';

    // Workflow domain invariant: a macro step's when tool returning true is what causes the engine
    // to expand a loop or route a branch to exit rebind.
    return slotInOwner === 'cases/when' ? 'branch_exit_rebind' : 'loop_continuation';
}

function pruneOrderedToolStepsAfterWinningBranchCase(args: {
    orderedToolSteps: MutableToolStep[];
    winningCaseIndexByOwner: ReadonlyMap<MutableBranchStepKey, number>;
    branchCaseIndexByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], number>;
    branchOwnerByToolStepKey: ReadonlyMap<MutableToolStep['stepKey'], MutableBranchStepKey>;
}): MutableToolStep[] {
    const {
        orderedToolSteps,
        winningCaseIndexByOwner,
        branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey,
    } = args;

    return orderedToolSteps.filter((toolStep) => {
        const ownerBranchStepKey = branchOwnerByToolStepKey.get(toolStep.stepKey as MutableToolStep['stepKey']);
        if (!ownerBranchStepKey) return true;

        const winningCaseIndex = winningCaseIndexByOwner.get(ownerBranchStepKey);
        if (winningCaseIndex === undefined) return true;

        const caseIndex = branchCaseIndexByToolStepKey.get(toolStep.stepKey as MutableToolStep['stepKey']);
        if (caseIndex === undefined) return true;

        return caseIndex <= winningCaseIndex;
    });
}

export function buildBaseTraceSnapshot(args: {
    mutableWorkflowTrace: MutableWorkflowTrace;
    selectedTraceHandle: string;
}): BaseTraceSnapshot {
    const { mutableWorkflowTrace, selectedTraceHandle } = args;
    const workflowStartTracePoint = mutableWorkflowTrace.tracePoints.find(
        (tracePoint) => tracePoint.tracePointKind === CONSTANTS.Enums.TracePointKind.workflow_start
    );
    const mutableWorkflow =
        workflowStartTracePoint
            && 'workflow' in workflowStartTracePoint
            && workflowStartTracePoint.workflow
            ? workflowStartTracePoint.workflow as MutableWorkflow
            : null;

    const tickTracePoints = mutableWorkflowTrace.tracePoints.filter(
        (tracePoint): tracePoint is MutableTickTracePoint => tracePoint.tracePointKind === 'tick'
    );

    const preToolPreludeTickCount = computePreToolPreludeTickCount({
        tickTracePoints,
    });

    const toolExecutionTickIndexes = tickTracePoints
        .map((tracePoint, index) => (
            tracePoint.tickKind === CONSTANTS.Enums.TickKind.tool_execution
                && tracePoint.stepKind === CONSTANTS.Enums.StepKind.tool
                ? index
                : -1
        ))
        .filter((index) => index >= 0);

    const authoredBranchToolStepControl = deriveBranchToolStepControlFromReplayTicks({
        initialMutableWorkflow: mutableWorkflow,
        tickTracePoints,
        appliedTickCount: 0,
    });

    return {
        selectedTraceHandle,
        mutableWorkflowTrace,
        tickTracePoints,
        timeline: {
            preToolPreludeTickCount,
            toolExecutionTickIndexes,
        },
        initialWorkflow: {
            mutableWorkflow,
            workflowState: deriveInitialWorkflowStateFromMutableTrace(mutableWorkflowTrace),
        },
        macroStepControl: {
            branch: {
                authored: {
                    branchToolStepKeyByAddress: authoredBranchToolStepControl.branchToolStepKeyByAddress,
                    branchCaseIndexByToolStepKey: authoredBranchToolStepControl.branchCaseIndexByToolStepKey,
                    branchOwnerByToolStepKey: authoredBranchToolStepControl.branchOwnerByToolStepKey,
                    branchWhatToolStepKeys: authoredBranchToolStepControl.branchWhatToolStepKeys,
                    branchWhenToolStepKeys: authoredBranchToolStepControl.branchWhenToolStepKeys,
                },
            },
        },
    };
}

export function buildTraceSnapshotFromBase(args: {
    baseSnapshot: BaseTraceSnapshot;
    replayPositionIndex: number | undefined;
    toolMap: ToolMap;
}): TraceSnapshot {
    const { baseSnapshot, replayPositionIndex, toolMap } = args;

    const replayPositionCount = baseSnapshot.timeline.toolExecutionTickIndexes.length;
    const appliedReplayPositionIndex = clampReplayPositionIndex(replayPositionIndex, replayPositionCount);
    const appliedTickCount = toAppliedTickCountForReplayPosition({
        tickTracePoints: baseSnapshot.tickTracePoints,
        preToolPreludeTickCount: baseSnapshot.timeline.preToolPreludeTickCount,
        toolExecutionTickIndexes: baseSnapshot.timeline.toolExecutionTickIndexes,
        appliedReplayPositionIndex,
    });
    const runtimeToBranchToolStepKey = buildRuntimeToBranchToolStepKey({
        tickTracePoints: baseSnapshot.tickTracePoints,
        appliedTickCount,
        branchToolStepKeyByAddress: baseSnapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress,
    });

    const orderedToolStepsBeforeBranchExitPrune = foldOrderedToolStepsToReplayPosition({
        initialMutableWorkflow: baseSnapshot.initialWorkflow.mutableWorkflow,
        tickTracePoints: baseSnapshot.tickTracePoints,
        appliedTickCount,
    });
    const toolStepByKey = toToolStepByKey(orderedToolStepsBeforeBranchExitPrune);

    const workflowState = foldWorkflowStateToReplayPosition({
        initialWorkflowState: baseSnapshot.initialWorkflow.workflowState,
        initialMutableWorkflow: baseSnapshot.initialWorkflow.mutableWorkflow,
        tickTracePoints: baseSnapshot.tickTracePoints,
        appliedTickCount,
        runtimeToBranchToolStepKey,
    });

    const winningCaseIndexByOwner = deriveWinningBranchCaseIndexByOwner({
        tickTracePoints: baseSnapshot.tickTracePoints,
        appliedTickCount,
        workflowState,
        toolStepByKey,
        toolMap,
        runtimeToBranchToolStepKey,
        branchCaseIndexByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchOwnerByToolStepKey,
    });
    const edgeEventKind = deriveEdgeEventKindForReplayPosition({
        tickTracePoints: baseSnapshot.tickTracePoints,
        toolExecutionTickIndexes: baseSnapshot.timeline.toolExecutionTickIndexes,
        appliedReplayPositionIndex,
        workflowState,
        toolStepByKey,
        toolMap,
        runtimeToBranchToolStepKey,
    });
    const reboundWorkflowState = applyBranchExitRebindToWorkflowState({
        workflowState,
        orderedToolSteps: orderedToolStepsBeforeBranchExitPrune,
        winningCaseIndexByOwner,
        toolStepByKey,
        branchToolStepKeyByAddress: baseSnapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress,
        branchCaseIndexByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchCaseIndexByToolStepKey,
        branchOwnerByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchOwnerByToolStepKey,
    });
    const { loopWhatToolStepKeys, loopWhenToolStepKeys } = deriveLoopToolStepSlotSetsFromReplayTicks({
        tickTracePoints: baseSnapshot.tickTracePoints,
        appliedTickCount,
    });

    return {
        selectedTraceHandle: baseSnapshot.selectedTraceHandle,
        mutableWorkflowTrace: baseSnapshot.mutableWorkflowTrace,
        tickTracePoints: baseSnapshot.tickTracePoints,
        timeline: {
            preToolPreludeTickCount: baseSnapshot.timeline.preToolPreludeTickCount,
            toolExecutionTickIndexes: baseSnapshot.timeline.toolExecutionTickIndexes,
            replayPositionCount,
        },
        edgeEventKind,
        position: {
            appliedReplayPositionIndex,
            orderedToolStepsBeforeBranchExitPrune,
            orderedToolSteps: pruneOrderedToolStepsAfterWinningBranchCase({
                orderedToolSteps: orderedToolStepsBeforeBranchExitPrune,
                winningCaseIndexByOwner,
                branchCaseIndexByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchCaseIndexByToolStepKey,
                branchOwnerByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchOwnerByToolStepKey,
            }),
            workflowState: reboundWorkflowState,
        },
        macroStepControl: {
            branch: {
                authored: {
                    branchWhatToolStepKeys: baseSnapshot.macroStepControl.branch.authored.branchWhatToolStepKeys,
                    branchWhenToolStepKeys: baseSnapshot.macroStepControl.branch.authored.branchWhenToolStepKeys,
                    branchOwnerByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchOwnerByToolStepKey,
                    branchCaseIndexByToolStepKey: baseSnapshot.macroStepControl.branch.authored.branchCaseIndexByToolStepKey,
                    branchToolStepKeyByAddress: baseSnapshot.macroStepControl.branch.authored.branchToolStepKeyByAddress,
                },
                replay: {
                    runtimeToBranchToolStepKey,
                    winningBranchCaseIndexByOwner: winningCaseIndexByOwner,
                },
            },
            loop: {
                loopWhatToolStepKeys,
                loopWhenToolStepKeys,
            },
        },
    };
}
